<template>
  <div class="interactive-can">
    <div class="interactive-can__flex-container">
      <div class="interactive-can__image-container"
        :class="{
          'interactive-can__image-container__image-container--hidden': !imagesLoaded
        }"
        @touchstart.prevent.stop
        @mousedown.prevent.stop>
        <ninja-loader transparent :show="!imagesLoaded"/>
         <transition-group name="fade" appear v-if="!mediaSmall && !animating">
          <InteraciveCanCallout
            class="interactive-can__callout"
            v-for="callout in activeCallouts"
            :key="callout.text"
            :style="callout.style"
            :text="callout.text"
            :side="callout.side"
          />
        </transition-group>
        <img
          v-for="image in canImages"
          :key="image"
          class="interactive-can__image-container__image"
          :class="{
            'interactive-can__image-container__image--visible': image === activeImage
          }"
          :src="image"
          :alt="canAlt"
        />
      </div>
    </div>
    <SliderInterface
      class="interactive-can__image-slider"
      :class="{
        'interactive-can__image-slider--hidden': !imagesLoaded
      }"
      :itemCount="imageCount"
      v-model="sliderIndex"
      @slideChange="handleSlideChange"
      @buttonClick="handleButtonClick"
      @stopAnimation="stopImageAnimation"
      :animating="animating"
    />
    <div class="interactive-can__small-callout-text-wrapper" v-if="mediaSmall">
      <div class="interactive-can__small-callout-text">
        <transition name="fade" appear>
          <span v-html="selectedCalloutText" v-if="showCalloutText" />
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * interactive can component
 */
import { mapState } from 'vuex';
import InteraciveCanCallout from './../components/interactive-can-callout';
import SliderInterface from './../components/slider-interface';
import ImagePreload from '../core/image-preload';
import CAN_SEQUENCE from './../core/can-sequence';
import {
  CONTENT_CAN_SECTION_CALLOUT_HEADBAND,
  CONTENT_CAN_SECTION_CALLOUT_PON_PON,
  CONTENT_CAN_SECTION_CALLOUT_LOGO,
  CONTENT_CAN_SECTION_CALLOUT_SIGNATURE_STYLE,
  CONTENT_CAN_SECTION_CAN_IMAGE_ALT,
} from '../../design-tokens/build/dictionary';

/**
 * Make style obj from single val
 *
 * @param {Number} top the top value
 */
function getStyleFromTop(top) {
  return {
    top: `${top}%`,
  };
}

export default {
  name: 'interactive-can',
  components: {
    SliderInterface,
    InteraciveCanCallout,
  },
  data() {
    return {
      sliderIndex: 18,
      canImages: CAN_SEQUENCE,
      selectedCalloutText: null,
      showCalloutText: false,
      canAlt: CONTENT_CAN_SECTION_CAN_IMAGE_ALT,
      imagesLoaded: false,
      imageInterval: null,
      animating: false,
      buttonPoints: [10, 15, 25, 30]
    };
  },

  mounted() {
    new ImagePreload(this.canImages)
      .load()
      .then(() => {
        this.imagesLoaded = true;
        this.startImageAnimation();
      });
  },
  computed: {
    /**
     * Last valid index, 0 is always the first
     *
     * @returns {number}
     */
    lastIndex() {
      return this.canImages.length - 1;
    },
    /**
     * return the image src
     *
     * @returns {String}
     */
    activeImage() {
      return this.canImages[this.sliderIndex];
    },
    /**
     * return the total number of can images
     *
     * @returns {Number}
     */
    imageCount() {
      return this.canImages.length;
    },

    closestToCurrentIndex() {
      return this.buttonPoints.reduce((prev, curr) => (Math.abs(curr - this.sliderIndex) < Math.abs(prev - this.sliderIndex) ? curr : prev));
    },

    /**
     * Used for setting computed styles on active callout
     */
    activeCalloutStyle() {
      return {
        top: this.callouts.top,
      };
    },

    /**
     * The active Can config
     */
    activeCallouts() {
      return [
        this.headbandCallout,
        this.ponPonCallout,
        this.logoCallout,
        this.signatureStyleCallout,
      ].filter(callout => !!callout);
    },

    /**
     * Headband callout config
     */
    headbandCallout() {
      if (!this.checkIndexRange(28, 39)) {
        return null;
      }

      return {
        text: CONTENT_CAN_SECTION_CALLOUT_HEADBAND,
        side: 'right',
        style: getStyleFromTop(15)
      };
    },

    /**
     * Pon Pon Dance callout config
     */
    ponPonCallout() {
      if (!this.checkIndexRange(0, 12)) {
        return null;
      }

      return {
        text: CONTENT_CAN_SECTION_CALLOUT_PON_PON,
        side: 'right',
        style: getStyleFromTop(58)
      };
    },

    /**
     * Logo callout config
     */
    logoCallout() {
      if (!this.checkIndexRange(13, 20)) {
        return null;
      }

      return {
        text: CONTENT_CAN_SECTION_CALLOUT_LOGO,
        side: 'right',
        style: getStyleFromTop(12)
      };
    },

    /**
     * Signature Style config
     */
    signatureStyleCallout() {
      let matchHeadband;
      let matchPonPon;

      if (this.checkIndexRange(20, 27)) {
        matchHeadband = true;
      }

      if (!matchPonPon && !matchHeadband) {
        return false;
      }

      const style = matchHeadband ? getStyleFromTop(32) : getStyleFromTop(70);

      return {
        text: CONTENT_CAN_SECTION_CALLOUT_SIGNATURE_STYLE,
        side: 'left',
        style,
      };
    },

    ...mapState('windowMedia', [
      'mediaSmall'
    ]),
  },

  watch: {
    mediaSmall(value) {
      if (!value) {
        // clear selected callout text when we go to a new breakpoint
        this.updateSelectedCalloutText('');
      }
    }
  },
  methods: {
    /**
     * Check if current index is in range
     *
     * @param {Number} start
     * @param {Number} end
     */
    checkIndexRange(start, end) {
      return this.sliderIndex <= end && this.sliderIndex >= start;
    },

    /**
     * increment the index of the current slide
     *
     * @param {String} direction - prev or next
     */
    handleSlideChange(direction) {
      this.showCalloutText = false;
      if (direction === 'next') {
        if (this.sliderIndex === this.lastIndex) {
          return;
        }
        this.sliderIndex += 1;
      } else if (this.sliderIndex !== 0) {
        this.sliderIndex -= 1;
      }
    },
    /**
     * handle a click on the interface buttons
     *
     * @param {*}
     * @param {String} direction - prev or next
     */
    handleButtonClick({ direction }) {
      let upcomingIndex;
      if (this.animating) {
        upcomingIndex = direction === 'prev' ? 10 : 30;
        this.stopImageAnimation(upcomingIndex);
      } else if (this.sliderIndex === 10 && direction === 'next') {
        this.sliderIndex = 15;
      } else if (this.sliderIndex === this.lastIndex && direction === 'prev') {
        this.sliderIndex = 25;
      } else {
        if ((this.sliderIndex === 10 && direction === 'prev') || (this.sliderIndex === 30 && direction === 'next')) return;
        if (this.buttonPoints.indexOf(this.sliderIndex) > -1) {
          let newIndex = this.buttonPoints.indexOf(this.sliderIndex);
          newIndex = direction === 'prev' ? this.buttonPoints[newIndex - 1] : this.buttonPoints[newIndex + 1];
          this.sliderIndex = newIndex;
        } else {
          this.sliderIndex = this.closestToCurrentIndex;
        }
      }
    },
    startImageAnimation() {
      let moveLeft = true;
      this.animating = true;
      this.interval = setInterval(() => {
        if (this.sliderIndex > 0 && moveLeft) {
          this.sliderIndex -= 1;
        } else if (this.sliderIndex < 39) {
          moveLeft = false;
          this.sliderIndex += 1;
        } else {
          moveLeft = true;
        }
      }, 60);
    },

    stopImageAnimation(index = 0) {
      clearInterval(this.interval);
      this.sliderIndex = index;
      this.animating = false;
    },
    /**
     * Update the displayed callout on small screens
     *
     * @param {string} text text to display
     */
    updateSelectedCalloutText(text) {
      this.selectedCalloutText = text;
      this.showCalloutText = true;
    },

    /**
     * handle tapping on an info icon
     *
     * @param {string} text text to display
     */
    handleInfoIconClick(text) {
      this.showCalloutText = false;
      this.$nextTick().then(() => this.updateSelectedCalloutText(text));
    }
  }
};
</script>

<style lang="scss">
.interactive-can {
  position: relative;
  text-align: center;

  &__flex-container {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }
  &__image-container {
    position: relative;
    transition: opacity 300ms ease;
    height: $size-height-interactive-can-image-large;
    width: $size-width-interactive-can-image-large;
    @include global-media(small) {
      height: $size-height-interactive-can-image-small;
      width: $size-width-interactive-can-image-small;
    }
    &--hidden {
      opacity: 0;
    }
    &__image {
      height: $size-height-interactive-can-image-large;
      position: absolute;
      left: 0;
      opacity: 0;
      &--visible {
        opacity: 1;
      }
      @include global-media(small) {
        height: $size-height-interactive-can-image-small;
      }
    }
  }
  &__image-slider {
    flex: 1 1 auto;
    margin: $size-margin-top-interactive-can-slider-large auto $size-margin-bottom-interactive-can-slider-large;
    transition: opacity 300ms ease;
    &--hidden {
      opacity: 0;
    }
    @include global-media(small) {
      margin: $size-margin-top-interactive-can-slider-small auto $size-margin-bottom-interactive-can-slider-small;
    }
  }
  &__info-icon {
    position: absolute;
    right: 100%;
    transform: translateY(-50%);
    z-index: $z-index-content;
    margin-right: $size-margin-interactive-can-info-icon;
    &--right.info-icon {
      right: initial;
      left: 100%;
      margin-left: $size-margin-interactive-can-info-icon;
    }
  }
  &__small-callout-text-wrapper {
    position: relative;
    width: 100%;
    height: $size-height-interactive-can-small-callout-text;
  }
  &__small-callout-text {
    position: absolute;
    width: 100%;
    top: 0;
    left: 0;

    color: $color-interactive-can-small-callout-text-text;
    font-size: $size-text-interactive-can-small-callout-text;
    font-family: $font-family-interactive-can-small-callout-text;
    font-weight: $font-weight-interactive-can-small-callout-text;
    margin-top: $size-margin-interactive-can-small-callout-text;
  }

}
</style>
